import type { OosaFlagDetails } from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { Button, cn } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import {
  formatOosaHoverDate,
  formatOosaPillDateRange,
} from '#src/components/oosa/oosaFlag.ts'
import { ParticipantFlagBadge } from '#src/components/ParticipantFlagBadge.tsx'

// Figma Facesheet OOSA AlertPill (node 1494:26253): solid #9F0712 + white.
// `badge-v2-red-solid` is that fill; restated under `dark:` so the dark theme
// cannot pull the pill back to the outline red swatch.
export const OOSA_PILL_CLASS_NAME =
  'border-transparent bg-badge-v2-red-solid font-semibold text-white dark:border-transparent dark:bg-badge-v2-red-solid dark:text-white'

export function OosaFlagHoverCard({
  details,
  onEdit,
  size,
  className,
  open,
}: {
  details?: OosaFlagDetails
  onEdit?: () => void
  size?: 'sm'
  className?: string
  open?: boolean
}): ReactNode {
  const flagDef = patients.PARTICIPANT_FLAGS[patients.ParticipantFlagCode.OOSA]
  const label = details
    ? `${flagDef.coding.display} (${formatOosaPillDateRange(details.startDate, details.returnDate)})`
    : flagDef.coding.display
  const badge = (
    <ParticipantFlagBadge
      flag={flagDef}
      label={label}
      size={size}
      className={cn(OOSA_PILL_CLASS_NAME, className)}
      disableTooltip
    />
  )

  if (!details) {
    return badge
  }

  return (
    <HoverCard open={open} openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="inline-flex items-center">{badge}</span>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-72 p-4">
        <OosaFlagDetailsCard details={details} onEdit={onEdit} />
      </HoverCardContent>
    </HoverCard>
  )
}

export function OosaFlagDetailsCard({
  details,
  onEdit,
}: {
  details: OosaFlagDetails
  onEdit?: () => void
}): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">OOSA (Out of Service Area)</p>
        {onEdit && (
          <Button
            type="button"
            variant="ghost"
            size="unstyled"
            className="size-6 shrink-0 text-muted-foreground"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onEdit()
            }}
            aria-label="Edit OOSA"
          >
            <IconPencil className="size-3" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex items-center justify-between gap-6">
          <span className="text-muted-foreground">Start Date</span>
          <span className="text-right">
            {formatOosaHoverDate(details.startDate)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-muted-foreground">Return Date</span>
          <span className="text-right">
            {details.returnDateUnknown
              ? 'Unknown'
              : formatOosaHoverDate(details.returnDate ?? details.startDate)}
          </span>
        </div>
      </div>
      <div className="h-px bg-border" />
      <div className="flex items-center justify-between gap-6 text-xs">
        <span className="text-muted-foreground">Reason for OOSA</span>
        <span className="text-right">
          {patients.formatOosaReasonDisplay(
            details.reason,
            details.otherReason
          )}
        </span>
      </div>
      {details.notes ? (
        <>
          <div className="h-px bg-border" />
          <div className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Notes</span>
            <span>{details.notes}</span>
          </div>
        </>
      ) : null}
    </div>
  )
}
